// src/pages/Relationships.jsx
//
// The pre-lead social layer. Everyone Claire has meaningfully exchanged
// something with on Twitter, Discord, Instagram, etc. -- not just leads,
// not just people she follows, but anyone who has actually talked back.
// Scored by engagement depth over time. Bradley reviews here and promotes
// the ones worth pursuing into the lead pipeline.
//
// Note: relationship_events (the actual content of each exchange, the
// original post she was responding to, etc.) populate as the platform
// pollers run. Until Twitter and Discord pollers are active and Instagram
// is live, this will be empty -- but the schema is ready.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const PLATFORM_STYLES = {
  twitter: "bg-sky-100 text-sky-800",
  discord: "bg-purple-100 text-purple-800",
  instagram: "bg-pink-100 text-pink-800",
  facebook: "bg-blue-100 text-blue-800",
};

const SCORE_TIERS = [
  { min: 20, label: "Hot", style: "bg-red-100 text-red-800" },
  { min: 8,  label: "Warm", style: "bg-amber-100 text-amber-800" },
  { min: 1,  label: "Cool", style: "bg-gray-100 text-gray-600" },
];

function scoreTier(score) {
  return SCORE_TIERS.find((t) => score >= t.min) ?? SCORE_TIERS[SCORE_TIERS.length - 1];
}

function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function EventRow({ ev }) {
  return (
    <div className={`border-l-2 pl-3 py-1 text-xs ${ev.direction === "inbound" ? "border-green-400" : "border-blue-400"}`}>
      <div className="flex items-center gap-2 text-gray-400">
        <span className="font-medium uppercase">{ev.event_type}</span>
        <span>·</span>
        <span>{ev.direction === "inbound" ? "from them" : "from Claire"}</span>
        <span className="ml-auto">{formatDateTime(ev.occurred_at)}</span>
      </div>
      {ev.context_content && (
        <div className="mt-0.5 text-gray-400 italic line-clamp-2">
          ↳ responding to: "{ev.context_content}"
        </div>
      )}
      {ev.context_url && !ev.context_content && (
        <div className="mt-0.5">
          <a href={ev.context_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
            View original post ↗
          </a>
        </div>
      )}
      {ev.content && (
        <div className="mt-0.5 text-gray-700 whitespace-pre-wrap line-clamp-3">{ev.content}</div>
      )}
    </div>
  );
}

function RelationshipCard({ rel, onPromote, onIgnore, promoting }) {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const tier = scoreTier(rel.engagement_score);

  async function loadEvents() {
    if (events.length > 0) return;
    setEventsLoading(true);
    const { data } = await supabase
      .from("relationship_events")
      .select("*")
      .eq("relationship_id", rel.id)
      .order("occurred_at", { ascending: false })
      .limit(20);
    setEvents(data || []);
    setEventsLoading(false);
  }

  function toggleExpand() {
    if (!expanded) loadEvents();
    setExpanded((v) => !v);
  }

  return (
    <div className="rounded bg-white p-4 shadow">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${PLATFORM_STYLES[rel.platform] || "bg-gray-100 text-gray-600"}`}>
            {rel.platform}
          </span>
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${tier.style}`}>{tier.label}</span>
          {rel.status === "promoted" && (
            <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Promoted</span>
          )}
          {rel.status === "ignored" && (
            <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">Ignored</span>
          )}
        </div>
        <div className="text-xs text-gray-400">Last active {formatDateTime(rel.last_interaction_at)}</div>
      </div>

      <div className="mt-2 flex items-center gap-2">
        {rel.profile_url ? (
          <a href={rel.profile_url} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 hover:underline">
            {rel.display_name || rel.handle || "Unknown"}
          </a>
        ) : (
          <span className="font-semibold">{rel.display_name || rel.handle || "Unknown"}</span>
        )}
        {rel.handle && rel.display_name && rel.handle !== rel.display_name && (
          <span className="text-sm text-gray-400">@{rel.handle}</span>
        )}
      </div>

      <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
        <span>{rel.interaction_count} interaction{rel.interaction_count !== 1 ? "s" : ""}</span>
        <span>Score: {rel.engagement_score}</span>
        <span>Since {formatDateTime(rel.first_seen_at)}</span>
      </div>

      {rel.notes && (
        <div className="mt-2 text-sm text-gray-600 italic">{rel.notes}</div>
      )}

      {rel.status === "promoted" && rel.lead_id && (
        <div className="mt-2 text-sm">
          <Link to={`/leads/${rel.lead_id}`} className="text-blue-600 hover:underline">
            View lead →
          </Link>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {rel.status === "tracking" && (
          <>
            <button
              type="button"
              disabled={promoting}
              onClick={() => onPromote(rel)}
              className="rounded bg-green-500 px-3 py-1.5 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {promoting ? "Promoting…" : "Promote to Lead"}
            </button>
            <button
              type="button"
              onClick={() => onIgnore(rel.id)}
              className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200"
            >
              Ignore
            </button>
          </>
        )}
        {rel.status === "ignored" && (
          <button
            type="button"
            onClick={() => onIgnore(rel.id, "tracking")}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200"
          >
            Unignore
          </button>
        )}
        <button
          type="button"
          onClick={toggleExpand}
          className="rounded bg-gray-50 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          {expanded ? "Hide history" : `Show history (${rel.interaction_count})`}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 flex flex-col gap-2">
          {eventsLoading ? (
            <div className="text-xs text-gray-400">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-xs text-gray-400">No recorded exchanges yet.</div>
          ) : (
            events.map((ev) => <EventRow key={ev.id} ev={ev} />)
          )}
        </div>
      )}
    </div>
  );
}

const PLATFORMS = ["all", "twitter", "discord", "instagram", "facebook"];
const STATUSES = ["tracking", "all", "promoted", "ignored"];
const SORTS = [
  { value: "score", label: "Engagement score" },
  { value: "recent", label: "Most recent" },
  { value: "count", label: "Interaction count" },
];

export default function Relationships() {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("tracking");
  const [sort, setSort] = useState("score");
  const [promotingId, setPromotingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    let q = supabase.from("relationships").select("*");
    if (platform !== "all") q = q.eq("platform", platform);
    if (status !== "all") q = q.eq("status", status);
    if (sort === "score") q = q.order("engagement_score", { ascending: false });
    else if (sort === "recent") q = q.order("last_interaction_at", { ascending: false, nullsFirst: false });
    else q = q.order("interaction_count", { ascending: false });
    const { data, error: fetchErr } = await q;
    if (fetchErr) setError(fetchErr.message);
    else setRelationships(data || []);
    setLoading(false);
  }, [platform, status, sort]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function promote(rel) {
    setPromotingId(rel.id);
    setError("");
    try {
      // Create the lead
      const { data: newLead, error: leadErr } = await supabase
        .from("leads")
        .insert({
          name: rel.display_name || rel.handle || rel.platform,
          profile_url: rel.profile_url,
          lead_source: rel.platform,
          discovered_by: "relationship",
          status: "New",
        })
        .select("id")
        .single();
      if (leadErr) throw leadErr;

      // Mark the relationship as promoted
      const { error: updateErr } = await supabase
        .from("relationships")
        .update({ status: "promoted", lead_id: newLead.id })
        .eq("id", rel.id);
      if (updateErr) throw updateErr;

      navigate(`/leads/${newLead.id}`);
    } catch (err) {
      setError(err.message);
      setPromotingId(null);
    }
  }

  async function setRelStatus(id, newStatus) {
    const { error: updateErr } = await supabase
      .from("relationships")
      .update({ status: newStatus })
      .eq("id", id);
    if (updateErr) setError(updateErr.message);
    else setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
  }

  const trackingCount = relationships.filter((r) => r.status === "tracking").length;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Relationships</h1>
            {trackingCount > 0 && status === "tracking" && (
              <p className="text-sm text-gray-500">{trackingCount} being tracked</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded border p-2 text-sm">
              {PLATFORMS.map((p) => <option key={p} value={p}>{p === "all" ? "All platforms" : p}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded border p-2 text-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded border p-2 text-sm">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          People Claire has meaningfully exchanged something with on social platforms — not yet leads, but worth watching.
          Scored by engagement depth (DM &gt; reply &gt; comment &gt; like &gt; follow).
          Promote anyone worth pursuing directly into the lead pipeline.
          {" "}<span className="italic">Pollers populate this automatically once Twitter and Discord are active. Eventually Claire will auto-graduate high-scoring relationships by DMing them — "Promote to Lead" is a manual override for now, during training.</span>
        </p>

        {error ? <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : relationships.length === 0 ? (
          <div className="rounded bg-white p-8 text-center shadow">
            <div className="text-gray-500 text-sm">No relationships yet.</div>
            <div className="mt-2 text-xs text-gray-400">
              This fills automatically as Claire interacts with people on Twitter, Discord, and Instagram.
              {platform !== "all" && " Try switching to 'All platforms'."}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {relationships.map((rel) => (
              <RelationshipCard
                key={rel.id}
                rel={rel}
                onPromote={promote}
                onIgnore={(id, newStatus = "ignored") => setRelStatus(id, newStatus)}
                promoting={promotingId === rel.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
